import React, {useCallback} from 'react';
import s from '../../../PersonalOffice/MyOrders/MyOrdersPopups/Edit/PopupManagerEdit.module.css';
import {OneServiceType} from '../../../types/TypesUserOrders';
import NewInfoCheckbox from '../../../PersonalOffice/MyOrders/MyOrdersPopups/InfoCheckBox/NewInfoCheckbox';
import {useFormikContext} from 'formik';
import {useGetOrderServiceListQuery} from '../../../redux/DepotSlice/depotOrdersApi';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import PreLoaderComponent from '../../../assets/Animations/PreLoaderComponent/PreLoaderComponent';
import {deliveryApi} from '../../../redux/deliveryApi';

// Константы для типов доставки
const DELIVERY_TYPES = {
	NOVA_POSHTA: {
		deliveryType: 'branch',
		deliveryTypeForNP: 'getWarehouses',
	},
	OTHER: {
		deliveryType: 'door',
		deliveryTypeForNP: 'getStreet',
	},
} as const;

// Поля адреса для сброса
const ADDRESS_FIELDS_TO_CLEAR = [
	'address_ref',
	'address_name',
	'building',
	'flat',
	'address_id',
] as const;

const DeliveryTypeSelector: React.FC = () => {
	const {values, setFieldValue} = useFormikContext<any>();
	const user = useSelector((state: RootState) => state.userData.userView);
	const {data: orderService, isLoading} = useGetOrderServiceListQuery(
		user?.location ?? '',
	);

	// Optional prefetch to warm the cache as user selects a type
	const prefetchAddresses = deliveryApi.usePrefetch('getAddressesList');

	// Проверка на Новую Почту
	const isNovaPoshtaService = useCallback((serviceName: string): boolean => {
		const lowerName = serviceName?.toLowerCase() || '';
		return lowerName.includes('нова') || lowerName.includes('nova');
	}, []);

	// Проверка на курьерскую доставку (временно отключена)
	// TODO: Убрать фильтрацию когда курьерские службы будут готовы к использованию
	const isCourierService = useCallback((serviceName: string): boolean => {
		const lowerName = serviceName?.toLowerCase() || '';
		return lowerName.includes('кур') || lowerName.includes('courier');
	}, []);

	// Фильтрация сервисов (временно исключаем курьерские службы)
	const filteredServices =
		orderService?.service_types?.filter((service: OneServiceType) => {
			return !isCourierService(service.service_name);
		}) || [];

	// Сброс полей адреса
	const clearAddressFields = useCallback(() => {
		ADDRESS_FIELDS_TO_CLEAR.forEach((field) => {
			setFieldValue(field, '');
		});
	}, [setFieldValue]);

	// Установка типа доставки по умолчанию
	const setDefaultDeliveryType = useCallback(
		(serviceName: string) => {
			const deliveryConfig = isNovaPoshtaService(serviceName)
				? DELIVERY_TYPES.NOVA_POSHTA
				: DELIVERY_TYPES.OTHER;

			setFieldValue('delivery_type', deliveryConfig.deliveryType);
			setFieldValue('delivery_typeForNP', deliveryConfig.deliveryTypeForNP);
		},
		[setFieldValue, isNovaPoshtaService],
	);

	// Обработчик выбора типа доставки
	const handleServiceSelection = useCallback(
		(service: OneServiceType) => {
			// Устанавливаем основные поля сервиса
			setFieldValue('service_token', service.service_token);
			setFieldValue('service_name', service.service_name);

			// Сбрасываем поля адреса
			clearAddressFields();

			// Устанавливаем тип доставки по умолчанию
			setDefaultDeliveryType(service.service_name);
		},
		[setFieldValue, clearAddressFields, setDefaultDeliveryType],
	);

	const handleDeliveryType = useCallback(
		(type: string) => {
			prefetchAddresses(type, {force: true});
		},
		[prefetchAddresses],
	);

	// Автоматический выбор сервиса, если service_type уже установлен
	React.useEffect(() => {
		if (values.service_type && orderService?.service_types && !isLoading) {
			const selectedService = orderService.service_types.find(
				(service: OneServiceType) => service.service_type === values.service_type
			);
			
			if (selectedService) {
				// Устанавливаем недостающие поля, если они не установлены
				if (!values.service_name || values.service_name !== selectedService.service_name) {
					setFieldValue('service_name', selectedService.service_name);
				}
				if (!values.service_token || values.service_token !== selectedService.service_token) {
					setFieldValue('service_token', selectedService.service_token);
				}
				
				// Устанавливаем тип доставки по умолчанию, если не установлен
				if (!values.delivery_type) {
					setDefaultDeliveryType(selectedService.service_name);
				}
			}
		}
	}, [values.service_type, values.service_name, values.service_token, values.delivery_type, orderService?.service_types, isLoading, setFieldValue, setDefaultDeliveryType]);

	if (isLoading) {
		return <PreLoaderComponent type={'gallery'} size={'big'} />;
	}

	return (
		<ul className={s.checkboxList}>
			{filteredServices.map((service: OneServiceType) => (
				<li
					role={'button'}
					tabIndex={0}
					key={service.service_type}
					className={s.checkboxElement}
					onClick={() => handleServiceSelection(service)}
				>
					<NewInfoCheckbox
						id={service.service_type}
						setFieldValue={setFieldValue}
						valueName={'service_type'}
						labelTxt={service.service_name}
						values={values}
						insideOnChange={handleDeliveryType}
					/>
				</li>
			))}
		</ul>
	);
};

export default DeliveryTypeSelector;
